/**
 * Partner-sourced connect flow (EPD-11850).
 *
 * A partner (e.g. Lovable) links creators to
 * `/connect?source=<partner>&return=<url>`. Both params are dedicated (not
 * UTMs) so they survive login, signup and the platform OAuth redirects intact.
 * Only sources listed here are honoured; any other value falls back to the
 * standard flow.
 *
 * `return` is where "Return to <partner>" sends the creator. It's only honoured
 * when it's an https URL on one of the partner's own hosts, so a shared link
 * can't be turned into an open redirect. A missing or rejected `return` falls
 * back to the partner's home page.
 *
 * A partner whose link goes through Atlas (`viaAtlas`) may also return to
 * Atlas's partner-connect return endpoint on one of the Atlas hosts for this
 * build (VITE_PARTNER_CONNECT_ATLAS_HOSTS). That return is signed by the API
 * with every account the creator has connected (see the partnerConnect router).
 */

import type { Platform } from "@/lib/queries/channel";
import { isAtlasReturnUrl, parseAtlasHosts } from "@repo/core";

export interface PartnerReturnHost {
  host: string;
  /**
   * Whether subdomains of `host` are allowed too; when false, `host` must
   * match exactly. Only for hosts the partner controls entirely: never for one
   * where the partner's users publish their own sites (e.g. `*.lovable.app`),
   * or anyone could host a page there.
   */
  subdomains: boolean;
}

export interface PartnerSourceConfig {
  id: string;
  name: string;
  /** Fallback for "Return to <partner>" when the link has no usable `return`. */
  homeUrl: string;
  /** Hosts a link's `return` URL may point at. */
  returnHosts: readonly PartnerReturnHost[];
  /** Platforms offered in this partner's flow, in display order. */
  platforms: readonly Platform[];
  /**
   * The partner's link goes through Atlas, so `return` may also be Atlas's
   * partner-connect return endpoint (EPD-11850).
   */
  viaAtlas: boolean;
}

export const PARTNER_SOURCES = {
  lovable: {
    id: "lovable",
    name: "Lovable",
    homeUrl: "https://creators.lovable.app/platform/me",
    // Lovable returns creators to https://creators.lovable.app/platform/me,
    // which is also where a missing or rejected `return` falls back to.
    // Other *.lovable.app hosts are apps Lovable's users publish, so only
    // that exact host is allowed.
    returnHosts: [{ host: "creators.lovable.app", subdomains: false }],
    platforms: ["instagram", "tiktok", "youtube"],
    viaAtlas: true,
  },
} as const satisfies Record<string, PartnerSourceConfig>;

export type PartnerSourceId = keyof typeof PARTNER_SOURCES;

/** Seconds the success screen waits before returning the creator to the partner. */
export const PARTNER_RETURN_COUNTDOWN_SECONDS = 60;

export function isPartnerSourceId(value: unknown): value is PartnerSourceId {
  return typeof value === "string" && Object.hasOwn(PARTNER_SOURCES, value);
}

export function getPartnerSource(value: unknown): PartnerSourceConfig | null {
  return isPartnerSourceId(value) ? PARTNER_SOURCES[value] : null;
}

/**
 * Reads the partner source carried inside a relative `returnTo` path, e.g.
 * `/connect?source=lovable` while the creator is on the login page.
 */
export function getPartnerSourceFromReturnTo(
  returnTo: string | undefined,
): PartnerSourceConfig | null {
  if (!returnTo) return null;
  const queryStart = returnTo.indexOf("?");
  if (queryStart === -1) return null;
  const params = new URLSearchParams(returnTo.slice(queryStart + 1));
  return getPartnerSource(params.get("source"));
}

// A local Atlas (http://localhost:10000) is accepted only in dev builds
const LOCAL_ATLAS_HOST = "localhost:10000";

/**
 * Atlas hosts whose partner-connect return endpoint this build accepts:
 * VITE_PARTNER_CONNECT_ATLAS_HOSTS (set per environment in
 * .github/workflows/main.yml, in step with the API's
 * PARTNER_CONNECT_ATLAS_HOSTS), plus the local Atlas in dev.
 */
export function getAtlasReturnHosts(): string[] {
  const hosts = parseAtlasHosts(
    import.meta.env.VITE_PARTNER_CONNECT_ATLAS_HOSTS,
  );
  if (import.meta.env.DEV && !hosts.includes(LOCAL_ATLAS_HOST)) {
    hosts.push(LOCAL_ATLAS_HOST);
  }
  return hosts;
}

/** True when `url` is Atlas's return endpoint and the partner goes through Atlas. */
export function isAtlasPartnerReturn(
  partner: PartnerSourceConfig,
  url: string | undefined,
  atlasHosts: readonly string[] = getAtlasReturnHosts(),
): boolean {
  return partner.viaAtlas && isAtlasReturnUrl(url, atlasHosts);
}

/** Longest `return` URL accepted from a link. */
export const MAX_RETURN_URL_LENGTH = 2000;

function isAllowedReturnHost(
  hostname: string,
  hosts: readonly PartnerReturnHost[],
): boolean {
  const host = hostname.toLowerCase();
  return hosts.some(
    (allowed) =>
      host === allowed.host ||
      (allowed.subdomains && host.endsWith(`.${allowed.host}`)),
  );
}

/**
 * The URL "Return to <partner>" should send the creator to: the link's
 * `return` when it's an https URL on one of the partner's hosts (default port,
 * no credentials) or, for a partner that goes through Atlas, Atlas's
 * partner-connect return endpoint; otherwise the partner's home page.
 */
export function resolvePartnerReturnUrl(
  partner: PartnerSourceConfig,
  requested: string | undefined,
  atlasHosts: readonly string[] = getAtlasReturnHosts(),
): string {
  if (!requested || requested.length > MAX_RETURN_URL_LENGTH)
    return partner.homeUrl;
  if (isAtlasPartnerReturn(partner, requested, atlasHosts))
    return new URL(requested).toString();
  let url: URL;
  try {
    url = new URL(requested);
  } catch {
    return partner.homeUrl;
  }
  if (
    url.protocol !== "https:" ||
    url.username ||
    url.password ||
    url.port !== ""
  )
    return partner.homeUrl;
  if (!isAllowedReturnHost(url.hostname, partner.returnHosts))
    return partner.homeUrl;
  return url.toString();
}
